// "Perfil individual de persona" — detalle de UN líder o supervisor sobre un
// período. Clona el patrón de LineaTiempoPage (cabecera con semáforo + back +
// período, KPIs con deltas, secciones con Recharts y tablas con scroll).
//
// Se llega desde C7 (/analisis-personal), que lista a TODOS. El backend detecta
// el rol del :userId y responde la sección correspondiente (lider | supervisor),
// más bloques comunes (KPIs, comparativo, posición, serie por día).
//
// URL: /coordinador/personal/:userId?desde&hasta
//   - :userId → useParams
//   - ?desde&hasta → useSearchParams (default: últimos 7 días).
//
// REGLA DE ORO (bonos sobre personas): producido = SOLO validado; esperado =
// capacidad estándar (no programa Alpina).

import { useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Bar,
  Cell,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { EtiquetaValor, pasoEtiquetas } from '../../components/charts/value-labels';
import {
  inicioSemanaOperativa,
  type CoordinadorPersonalPerfil,
  type PersonalPerfilSerieDia,
} from 'schemas';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { DirectivoLayout } from '../../components/layout/DirectivoLayout';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Icon } from '../../components/ui/icon';
import { InfoHint } from '../../components/ui/info-hint';
import { KpiCard } from '../../components/ui/kpi-card';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TFoot, TH, THead, TR, Table } from '../../components/ui/table';
import { useCoordinadorPersonalPerfil } from '../../lib/api/queries';
import { useTenantConfig } from '../../lib/api/useTenantConfig';
import { useAuth } from '../../lib/auth';
import { useVerComoActivo } from '../../lib/ver-como';
import { cn } from '../../lib/utils';
import { semaforoCumplimiento, toneTextClass, type Tone } from '../../lib/semaforo';

// =============================================================================
// Helpers de formato (es-CO) — clonados de LineaTiempoPage
// =============================================================================

function fmtNumero(n: number): string {
  return Math.round(n).toLocaleString('es-CO');
}

function fmtPct(n: number | null, decimals = 1): string {
  if (n === null) return '—';
  return `${n.toFixed(decimals)}%`;
}

function fmtFechaCorta(iso: string): string {
  if (!iso || iso.length < 10) return iso;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'short', timeZone: 'UTC' })
    .format(new Date(Date.UTC(y, m - 1, d)))
    .replace(/\.|,/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Período legible: "1 jun – 7 jun" o "7 jun" si desde === hasta. */
function fmtPeriodo(desde: string, hasta: string): string {
  if (desde === hasta) return fmtFechaCorta(desde);
  return `${fmtFechaCorta(desde)} – ${fmtFechaCorta(hasta)}`;
}

/** Fecha local YYYY-MM-DD. */
function ymd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Iniciales para el avatar. */
function iniciales(nombre: string, apellido: string): string {
  const a = nombre.trim()[0] ?? '';
  const b = apellido.trim()[0] ?? '';
  return `${a}${b}`.toUpperCase() || '·';
}

function toneDot(tone: Tone): string {
  return tone === 'ok'
    ? 'bg-ok'
    : tone === 'warn'
      ? 'bg-warn'
      : tone === 'crit'
        ? 'bg-crit'
        : 'bg-fg-tertiary';
}

function toneBarClass(tone: Tone): string {
  return tone === 'ok'
    ? 'bg-ok'
    : tone === 'warn'
      ? 'bg-warn'
      : tone === 'crit'
        ? 'bg-crit'
        : 'bg-fg-tertiary';
}

function toneVar(tone: Tone): string {
  return tone === 'ok'
    ? 'var(--color-ok)'
    : tone === 'warn'
      ? 'var(--color-warn)'
      : tone === 'crit'
        ? 'var(--color-crit)'
        : 'var(--color-fg-tertiary)';
}

// =============================================================================
// Textos de ayuda contextual
// =============================================================================

const HINT = {
  producido: 'Paquetes producidos y validados en el período. Solo cuenta lo validado.',
  esperado: 'Lo que podía producir a capacidad estándar en las horas que cubrió.',
  cumplimiento: 'Producido ÷ Esperado (capacidad). Verde ≥90, ámbar 70-89, rojo <70.',
  oportunidad: 'Tiempo promedio entre que el líder captura y el supervisor valida.',
  calidad:
    '% validado / % invalidado de sus registros. Puntualidad: lag medio entre el inicio del bloque y la captura (positivo = capturó tarde). Fuera de turno: capturas hechas fuera de su turno.',
} as const;

// =============================================================================
// Componente principal
// =============================================================================

type Periodo = 'hoy' | 'semana' | 'mes' | 'rango';

export function PersonaPerfilPage() {
  const { state, signOut } = useAuth();
  const { userId } = useParams<{ userId: string }>();
  // El directivo llega acá desde SU pantalla Personal: se le sirve el shell de
  // su mundo (sidebar del directivo, volver a /directivo/personal) — antes el
  // drill lo tiraba al layout del coordinador y el "volver" lo perdía. PERO en
  // modo "Ver como" está suplantando a un coordinador → shell de coordinador.
  const verComoActivo = useVerComoActivo();
  const esDirectivo =
    state.kind === 'signed_in' && state.claims.rol === 'directivo' && !verComoActivo;
  const envolver = (children: ReactNode) =>
    esDirectivo ? (
      <DirectivoLayout>{children}</DirectivoLayout>
    ) : (
      <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
        {children}
      </RoleLayout>
    );
  const [searchParams, setSearchParams] = useSearchParams();
  const { diaInicioSemana } = useTenantConfig();

  const { desde, hasta } = useMemo(() => {
    const d = searchParams.get('desde');
    const h = searchParams.get('hasta');
    if (d && h) return { desde: d, hasta: h };
    // Default: últimos 7 días (hoy-6 → hoy).
    const hoy = new Date();
    const inicio = new Date(hoy);
    inicio.setDate(hoy.getDate() - 6);
    return { desde: ymd(inicio), hasta: ymd(hoy) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Período activo derivado del rango actual (para resaltar el segmented).
  const periodoActivo: Periodo = useMemo(() => {
    const hoy = new Date();
    const hoyStr = ymd(hoy);
    if (desde === hoyStr && hasta === hoyStr) return 'hoy';
    if (desde === inicioSemanaOperativa(hoyStr, diaInicioSemana) && hasta === hoyStr)
      return 'semana';
    const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    if (desde === ymd(primerDia) && hasta === hoyStr) return 'mes';
    return 'rango';
  }, [desde, hasta, diaInicioSemana]);

  const setRango = (d: string, h: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('desde', d);
      next.set('hasta', h);
      return next;
    });
  };

  const cambiarPeriodo = (p: Periodo) => {
    const hoy = new Date();
    const hoyStr = ymd(hoy);
    if (p === 'hoy') return setRango(hoyStr, hoyStr);
    if (p === 'semana') return setRango(inicioSemanaOperativa(hoyStr, diaInicioSemana), hoyStr);
    if (p === 'mes') {
      const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      return setRango(ymd(primerDia), hoyStr);
    }
    // 'rango' — últimos 7 días como base editable.
    const inicio = new Date(hoy);
    inicio.setDate(hoy.getDate() - 6);
    return setRango(ymd(inicio), hoyStr);
  };

  const { data, isLoading, isError, error, refetch, isFetching } = useCoordinadorPersonalPerfil(
    userId ?? null,
    { desde, hasta },
  );

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return envolver(
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-tertiary">Cargando persona…</p>
      </div>,
    );
  }

  if (isError || !data) {
    return envolver(
      <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center gap-3 text-center">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar la persona</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
        <Button onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? 'Reintentando…' : 'Reintentar'}
        </Button>
        <button onClick={() => signOut()} className="text-xs text-fg-tertiary underline">
          Cerrar sesión
        </button>
      </div>,
    );
  }

  return envolver(
    <div className="flex flex-col gap-4">
      <Link
        to={esDirectivo ? '/directivo/personal' : '/coordinador/analisis-personal'}
        className="inline-flex w-fit items-center gap-1 text-sm text-fg-secondary transition-colors hover:text-fg"
      >
        <Icon name="chevron-left" size={16} />
        Volver a Personal
      </Link>
      <Cabecera data={data} periodo={periodoActivo} onPeriodo={cambiarPeriodo} />
      <KpisRow data={data} />
      <CumplimientoEnElTiempo data={data} />
      {data.lider ? <SeccionesLider lider={data.lider} /> : null}
      {data.supervisor ? <SeccionesSupervisor supervisor={data.supervisor} /> : null}
      <footer className="text-center text-[11px] text-fg-tertiary">{data.persona.email}</footer>
    </div>,
  );
}

// =============================================================================
// 1. Cabecera (avatar + nombre + subtexto por rol + semáforo + período)
// =============================================================================

function Cabecera({
  data,
  periodo,
  onPeriodo,
}: {
  data: CoordinadorPersonalPerfil;
  periodo: Periodo;
  onPeriodo: (p: Periodo) => void;
}) {
  const { persona, kpis } = data;
  const tone = semaforoCumplimiento(kpis.cumplimiento_meta_pct);

  const subtexto =
    persona.rol === 'lider'
      ? `Líder${[persona.area_nombre, persona.linea_nombre]
          .filter(Boolean)
          .map((s) => ` · ${s}`)
          .join('')}`
      : `Supervisor${persona.plantas.length > 0 ? ` · ${persona.plantas.join(', ')}` : ''}`;

  return (
    <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-3">
      <div className="flex items-center gap-3">
        <span
          aria-hidden
          className="flex size-10 flex-shrink-0 items-center justify-center rounded-full bg-surface-overlay text-sm font-semibold text-fg-secondary"
        >
          {iniciales(persona.nombre, persona.apellido)}
        </span>
        <div className="flex items-start gap-2.5">
          <span
            aria-hidden
            className={cn('mt-2 inline-block size-2.5 flex-shrink-0 rounded-full', toneDot(tone))}
          />
          <div>
            <h1 className="font-serif text-2xl font-medium leading-tight text-fg">
              {persona.nombre} {persona.apellido}
            </h1>
            <p className="mt-1 text-[11px] text-fg-tertiary">{subtexto}</p>
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end gap-2">
        <SegmentedControl<Periodo>
          size="sm"
          value={periodo}
          onChange={onPeriodo}
          options={[
            { value: 'hoy', label: 'Hoy' },
            { value: 'semana', label: 'Esta semana' },
            { value: 'mes', label: 'Este mes' },
            { value: 'rango', label: 'Rango' },
          ]}
        />
        <p className="text-[11px] text-fg-secondary">
          {fmtPeriodo(data.periodo.desde, data.periodo.hasta)}
        </p>
      </div>
    </header>
  );
}

// =============================================================================
// 2. KPIs comunes (con deltas del comparativo + posición)
// =============================================================================

interface DeltaDisplay {
  direction: 'up' | 'down' | 'flat';
  text: string;
  tone: Tone;
}

/**
 * Delta a partir de un valor crudo (% o pp).
 *  - kind 'pct' → porcentaje con signo; 'pp' → puntos porcentuales.
 *  - mayorEsMejor: true (cumplimiento/producido/horas) → subir = verde.
 */
function delta(
  raw: number | null,
  kind: 'pct' | 'pp',
  mayorEsMejor: boolean,
): DeltaDisplay | undefined {
  if (raw === null) return undefined;
  const direction = raw > 0 ? 'up' : raw < 0 ? 'down' : 'flat';
  const positivoBueno = mayorEsMejor ? raw > 0 : raw < 0;
  const tone: Tone = raw === 0 ? 'neutral' : positivoBueno ? 'ok' : 'crit';
  const signo = raw > 0 ? '+' : '';
  const text = kind === 'pp' ? `${signo}${raw.toFixed(1)} pp` : `${signo}${raw.toFixed(1)}%`;
  return { direction, text, tone };
}

function trendProp(d: DeltaDisplay | undefined) {
  if (!d) return undefined;
  return {
    direction: d.direction,
    text: (
      <>
        {d.text} <span className="font-normal text-fg-tertiary">vs período ant.</span>
      </>
    ),
    tone: d.tone,
  } as const;
}

function KpisRow({ data }: { data: CoordinadorPersonalPerfil }) {
  const { kpis: k, comparativo, posicion } = data;
  const metaTone = semaforoCumplimiento(k.cumplimiento_meta_pct);

  return (
    <section className="flex flex-col gap-3">
      <Card>
        <CardContent className="grid grid-cols-2 gap-4 p-5 sm:grid-cols-3 lg:grid-cols-5">
          <KpiCard
            label="Producido"
            hint={HINT.producido}
            value={fmtNumero(k.producido)}
            valueSuffix="paq"
            trend={trendProp(delta(comparativo?.delta_producido_pct ?? null, 'pct', true))}
          />
          <KpiCard
            label="Esperado"
            hint={HINT.esperado}
            value={fmtNumero(k.esperado_capacidad)}
            valueSuffix="paq"
            sub="capacidad estándar"
            divider
          />
          <KpiCard
            label="Cumplimiento"
            hint={HINT.cumplimiento}
            value={k.cumplimiento_meta_pct !== null ? k.cumplimiento_meta_pct.toFixed(0) : '—'}
            valueSuffix={k.cumplimiento_meta_pct !== null ? '%' : ''}
            trend={trendProp(delta(comparativo?.delta_cumplimiento_pp ?? null, 'pp', true))}
            subTone={metaTone === 'neutral' ? 'neutral' : metaTone}
            divider
          />
          <KpiCard
            label="Horas"
            value={String(k.horas)}
            trend={trendProp(delta(comparativo?.delta_horas_pct ?? null, 'pct', true))}
            divider
          />
          <KpiCard label="Días" value={String(k.dias)} divider />
        </CardContent>
      </Card>

      {posicion ? (
        <Card>
          <CardContent className="flex items-center justify-between gap-3 p-4">
            <div className="flex flex-col">
              <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Posición
              </span>
              <span className="text-[11px] text-fg-secondary">en {posicion.ambito}</span>
            </div>
            <span className="text-[22px] font-semibold tabular-nums text-fg">
              #{posicion.rank}
              <span className="text-[13px] font-normal text-fg-tertiary"> de {posicion.total}</span>
            </span>
          </CardContent>
        </Card>
      ) : null}
    </section>
  );
}

// =============================================================================
// 3. Cumplimiento en el tiempo (Gráfico / Tabla)
// =============================================================================

type Vista = 'grafico' | 'tabla';

function CumplimientoEnElTiempo({ data }: { data: CoordinadorPersonalPerfil }) {
  const [vista, setVista] = useState<Vista>('grafico');

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-medium text-fg">Cumplimiento en el tiempo</h2>
          <SegmentedControl<Vista>
            size="sm"
            value={vista}
            onChange={setVista}
            options={[
              { value: 'grafico', label: 'Gráfico' },
              { value: 'tabla', label: 'Tabla' },
            ]}
          />
        </div>
        {vista === 'grafico' ? (
          <SerieGrafico serie={data.serie_dia} />
        ) : (
          <SerieTabla serie={data.serie_dia} />
        )}
      </CardContent>
    </Card>
  );
}

function SerieGrafico({ serie }: { serie: PersonalPerfilSerieDia[] }) {
  const chartData = useMemo(
    () =>
      serie.map((p) => ({
        label: fmtFechaCorta(p.fecha),
        producido: Math.round(p.producido),
        esperado: Math.round(p.esperado),
        tone: semaforoCumplimiento(p.cumplimiento_meta_pct),
      })),
    [serie],
  );

  if (chartData.length === 0) {
    return <p className="py-10 text-center text-sm text-fg-tertiary">Sin datos en el período.</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="h-[280px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 16, right: 16, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
              axisLine={{ stroke: 'var(--color-border)' }}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tickFormatter={(v) => fmtNumero(Number(v))}
              tick={{ fontSize: 10.5, fill: 'var(--color-fg-tertiary)' }}
              axisLine={false}
              tickLine={false}
              width={48}
            />
            <Tooltip
              contentStyle={{
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(value, name) => [`${fmtNumero(Number(value))} paq`, name as string]}
            />
            <Bar dataKey="producido" name="Producido" radius={[3, 3, 0, 0]} fillOpacity={0.85}>
              {chartData.map((d, i) => (
                <Cell key={i} fill={toneVar(d.tone)} />
              ))}
              <LabelList
                content={
                  <EtiquetaValor
                    formatter={(v) => fmtNumero(v)}
                    paso={pasoEtiquetas(chartData.length)}
                    total={chartData.length}
                    fontSize={9.5}
                  />
                }
              />
            </Bar>
            <Line
              type="monotone"
              dataKey="esperado"
              name="Esperado"
              stroke="var(--color-fg-secondary)"
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-4 text-[10.5px] text-fg-tertiary">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-fg-tertiary" />
          Producido (color por semáforo)
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-0 w-5"
            style={{ borderTop: '2px dashed var(--color-fg-secondary)' }}
          />
          Esperado (capacidad)
        </span>
      </div>
    </div>
  );
}

function SerieTabla({ serie }: { serie: PersonalPerfilSerieDia[] }) {
  const totales = useMemo(
    () =>
      serie.reduce(
        (acc, d) => {
          acc.producido += d.producido;
          acc.esperado += d.esperado;
          acc.horas += d.horas;
          return acc;
        },
        { producido: 0, esperado: 0, horas: 0 },
      ),
    [serie],
  );

  if (serie.length === 0) {
    return <p className="py-10 text-center text-sm text-fg-tertiary">Sin datos en el período.</p>;
  }

  const totPct = totales.esperado > 0 ? (totales.producido / totales.esperado) * 100 : null;

  return (
    <Table maxHeight="70vh">
      <THead sticky>
        <TR>
          <TH>Día</TH>
          <TH align="right">Producido</TH>
          <TH align="right">Esperado</TH>
          <TH align="right">Cumplimiento</TH>
          <TH align="right">Horas</TH>
        </TR>
      </THead>
      <TBody>
        {serie.map((d) => {
          const tone = semaforoCumplimiento(d.cumplimiento_meta_pct);
          return (
            <TR key={d.fecha}>
              <TD>
                <span className="font-medium text-fg">{fmtFechaCorta(d.fecha)}</span>
              </TD>
              <TD align="right" numeric>
                {fmtNumero(d.producido)}
              </TD>
              <TD align="right" numeric className="text-fg-secondary">
                {fmtNumero(d.esperado)}
              </TD>
              <TD align="right" numeric>
                <span className={cn('font-medium', toneTextClass(tone))}>
                  {fmtPct(d.cumplimiento_meta_pct, 0)}
                </span>
              </TD>
              <TD align="right" numeric className="text-fg-secondary">
                {d.horas}
              </TD>
            </TR>
          );
        })}
      </TBody>
      <TFoot>
        <TR className="hover:bg-transparent">
          <TD className="font-medium text-fg">TOTAL</TD>
          <TD align="right" numeric className="font-medium text-fg">
            {fmtNumero(totales.producido)}
          </TD>
          <TD align="right" numeric>
            {fmtNumero(totales.esperado)}
          </TD>
          <TD align="right" numeric>
            <span className={cn('font-medium', toneTextClass(semaforoCumplimiento(totPct)))}>
              {fmtPct(totPct, 0)}
            </span>
          </TD>
          <TD align="right" numeric>
            {totales.horas}
          </TD>
        </TR>
      </TFoot>
    </Table>
  );
}

// =============================================================================
// 4. Secciones del LÍDER
// =============================================================================

function SeccionesLider({ lider }: { lider: NonNullable<CoordinadorPersonalPerfil['lider']> }) {
  return (
    <>
      <PorLineaSku porSku={lider.por_sku} />
      <CalidadCaptura calidad={lider.calidad} anomalias={lider.anomalias} />
      <Trazabilidad traz={lider.trazabilidad} />
    </>
  );
}

function PorLineaSku({
  porSku,
}: {
  porSku: NonNullable<CoordinadorPersonalPerfil['lider']>['por_sku'];
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-3">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-sm font-medium text-fg">Por línea / SKU que operó</h2>
          <p className="text-[11px] text-fg-tertiary">
            No se castiga a quien operó líneas difíciles.
          </p>
        </div>
        {porSku.length === 0 ? (
          <p className="py-8 text-center text-sm text-fg-tertiary">Sin SKUs en el período.</p>
        ) : (
          <Table maxHeight="600px">
            <THead sticky>
              <TR>
                <TH>Línea · SKU</TH>
                <TH align="right">Producido</TH>
                <TH align="right">Esperado</TH>
                <TH align="right">Cumplimiento</TH>
                <TH align="right">Horas</TH>
              </TR>
            </THead>
            <TBody>
              {porSku.map((s, i) => {
                const tone = semaforoCumplimiento(s.cumplimiento_meta_pct);
                const ancho = Math.min(100, Math.max(0, s.cumplimiento_meta_pct ?? 0));
                return (
                  <TR key={`${s.linea_codigo}-${s.sku_codigo}-${i}`}>
                    <TD>
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-fg">{s.linea_codigo}</span>
                        <span className="text-fg-tertiary">·</span>
                        <span className="text-fg-secondary">{s.sku_codigo}</span>
                      </div>
                      <div className="text-[10.5px] text-fg-tertiary">{s.sku_descripcion}</div>
                    </TD>
                    <TD align="right" numeric>
                      {fmtNumero(s.producido)}
                    </TD>
                    <TD align="right" numeric className="text-fg-secondary">
                      {fmtNumero(s.esperado)}
                    </TD>
                    <TD align="right" numeric>
                      <div className="flex items-center justify-end gap-2">
                        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-surface-overlay">
                          <div
                            className={cn('h-full rounded-full', toneBarClass(tone))}
                            style={{ width: `${ancho}%` }}
                          />
                        </div>
                        <span className={cn('font-medium', toneTextClass(tone))}>
                          {fmtPct(s.cumplimiento_meta_pct, 0)}
                        </span>
                      </div>
                    </TD>
                    <TD align="right" numeric className="text-fg-secondary">
                      {s.horas}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function CalidadCaptura({
  calidad,
  anomalias,
}: {
  calidad: NonNullable<CoordinadorPersonalPerfil['lider']>['calidad'];
  anomalias: NonNullable<CoordinadorPersonalPerfil['lider']>['anomalias'];
}) {
  const anomList: Array<[string, number]> = [
    ['P1', anomalias.p1],
    ['P2', anomalias.p2],
    ['P3', anomalias.p3],
    ['P4', anomalias.p4],
    ['P5', anomalias.p5],
    ['P6', anomalias.p6],
  ];

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-3">
        <div className="flex items-center gap-1.5">
          <h2 className="text-sm font-medium text-fg">Calidad de captura</h2>
          <InfoHint label="Calidad de captura">{HINT.calidad}</InfoHint>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Tile
            label="% validado"
            value={fmtPct(calidad.pct_validado, 0)}
            tone={
              calidad.pct_validado === null ? 'neutral' : calidad.pct_validado >= 90 ? 'ok' : 'warn'
            }
          />
          <Tile
            label="% invalidado"
            value={fmtPct(calidad.pct_invalidado, 0)}
            tone={
              calidad.pct_invalidado !== null && calidad.pct_invalidado > 0 ? 'crit' : 'neutral'
            }
          />
          <Tile
            label="Puntualidad"
            value={
              calidad.puntualidad_min === null
                ? '—'
                : `${calidad.puntualidad_min > 0 ? '+' : ''}${Math.round(calidad.puntualidad_min)} min`
            }
            tone={
              calidad.puntualidad_min !== null && calidad.puntualidad_min > 0 ? 'warn' : 'neutral'
            }
            sub="lag medio"
          />
          <Tile
            label="Fuera de turno"
            value={String(calidad.fuera_turno)}
            tone={calidad.fuera_turno > 0 ? 'warn' : 'neutral'}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            Anomalías
          </span>
          <div className="flex flex-wrap gap-1.5">
            {anomList.map(([nombre, n]) => {
              const tone: Tone = n === 0 ? 'neutral' : n >= 3 ? 'crit' : 'warn';
              return (
                <span
                  key={nombre}
                  className={cn(
                    'inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium tabular-nums',
                    tone === 'neutral'
                      ? 'bg-surface-overlay text-fg-tertiary'
                      : tone === 'warn'
                        ? 'bg-warn/15 text-warn'
                        : 'bg-crit/15 text-crit',
                  )}
                >
                  {nombre} {n}
                </span>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Tile({
  label,
  value,
  tone = 'neutral',
  sub,
}: {
  label: string;
  value: string;
  tone?: Tone;
  sub?: string;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-border bg-surface-overlay/40 p-3">
      <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        {label}
      </span>
      <span
        className={cn(
          'text-[20px] font-semibold leading-none tabular-nums',
          tone === 'neutral' ? 'text-fg' : toneTextClass(tone),
        )}
      >
        {value}
      </span>
      {sub ? <span className="text-[10.5px] text-fg-tertiary">{sub}</span> : null}
    </div>
  );
}

function Trazabilidad({
  traz,
}: {
  traz: NonNullable<CoordinadorPersonalPerfil['lider']>['trazabilidad'];
}) {
  const filas: Array<{ label: string; value: string; hint?: string }> = [
    { label: 'Registros totales', value: fmtNumero(traz.registros_totales) },
    {
      label: 'Registros validados',
      value: fmtNumero(traz.registros_validados),
      hint: 'Solo validados cuentan.',
    },
    {
      label: 'Ediciones post-captura',
      value: fmtNumero(traz.ediciones_post_captura),
      hint: 'Trazadas en auditoría.',
    },
    {
      label: 'Validó',
      value: traz.validado_por.length > 0 ? traz.validado_por.join(' · ') : '—',
    },
  ];

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-3">
        <div className="flex flex-col gap-0.5">
          <h2 className="text-sm font-medium text-fg">Trazabilidad</h2>
          <p className="text-[11px] text-fg-tertiary">Defendibilidad del número.</p>
        </div>
        <dl className="flex flex-col">
          {filas.map((f) => (
            <div
              key={f.label}
              className="flex items-center justify-between gap-3 border-b border-border py-2 last:border-b-0"
            >
              <dt className="flex items-center gap-1 text-[12.5px] text-fg-secondary">
                {f.label}
                {f.hint ? <span className="text-[11px] text-fg-tertiary">— {f.hint}</span> : null}
              </dt>
              <dd className="text-right text-sm font-medium tabular-nums text-fg">{f.value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// 5. Secciones del SUPERVISOR
// =============================================================================

function SeccionesSupervisor({
  supervisor,
}: {
  supervisor: NonNullable<CoordinadorPersonalPerfil['supervisor']>;
}) {
  const { intervencion } = supervisor;
  const turnos = useMemo(
    () => [...supervisor.turnos_detalle].sort((a, b) => b.fecha.localeCompare(a.fecha)),
    [supervisor.turnos_detalle],
  );

  return (
    <>
      <Card>
        <CardContent className="flex flex-col gap-3 p-3">
          <div className="flex items-center gap-1.5">
            <h2 className="text-sm font-medium text-fg">Gestión del turno</h2>
            <InfoHint label="Oportunidad">{HINT.oportunidad}</InfoHint>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Tile
              label="Oportunidad"
              value={
                supervisor.oportunidad_min === null
                  ? '—'
                  : `+${Math.round(supervisor.oportunidad_min)} min`
              }
              sub="captura → validación"
            />
            <Tile
              label="% validado turno"
              value={fmtPct(supervisor.pct_validado_turno, 0)}
              tone={
                supervisor.pct_validado_turno === null
                  ? 'neutral'
                  : supervisor.pct_validado_turno >= 90
                    ? 'ok'
                    : 'warn'
              }
            />
            <Tile label="Validaciones" value={fmtNumero(intervencion.validaciones)} tone="ok" />
            <Tile
              label="Invalidaciones"
              value={fmtNumero(intervencion.invalidaciones)}
              tone={intervencion.invalidaciones > 0 ? 'warn' : 'neutral'}
            />
            {/* El campo se llama `paros_cerrados` por historia (renombrarlo toca
                4 schemas + 3 dashboards), pero desde la mig 079 cuenta los paros
                que ESTE supervisor CONFIRMÓ — cerrar es del líder o del técnico. */}
            <Tile label="Paros confirmados" value={fmtNumero(intervencion.paros_cerrados)} />
            <Tile label="Delegaciones" value={fmtNumero(intervencion.capturas_delegadas)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-3 p-3">
          <h2 className="text-sm font-medium text-fg">Turnos cubiertos</h2>
          {turnos.length === 0 ? (
            <p className="py-8 text-center text-sm text-fg-tertiary">Sin turnos en el período.</p>
          ) : (
            <Table maxHeight="600px">
              <THead sticky>
                <TR>
                  <TH>Fecha</TH>
                  <TH>Turno</TH>
                  <TH>Planta</TH>
                  <TH align="right">Producido</TH>
                  <TH align="right">Esperado</TH>
                  <TH align="right">Validaciones</TH>
                </TR>
              </THead>
              <TBody>
                {turnos.map((t, i) => (
                  <TR key={`${t.fecha}-${t.turno_nombre}-${i}`}>
                    <TD>
                      <span className="font-medium text-fg">{fmtFechaCorta(t.fecha)}</span>
                    </TD>
                    <TD className="text-fg-secondary">{t.turno_nombre}</TD>
                    <TD className="text-fg-secondary">{t.planta_nombre}</TD>
                    <TD align="right" numeric>
                      {fmtNumero(t.producido)}
                    </TD>
                    <TD align="right" numeric className="text-fg-secondary">
                      {fmtNumero(t.esperado)}
                    </TD>
                    <TD align="right" numeric className="text-ok">
                      {t.validaciones}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}
